"use client";

import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { GROQButton } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { PipeChip } from "./_shared";
import { ICompareRow } from "@/sanity/pages/PageHomeTemplate";

interface ComparisonSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  othersLabel?: string;
  wceLabel?: string;
  rows?: ICompareRow[];
  othersFooter?: string;
  cta?: GROQButton;
}

const CrossIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-[2.25rem] w-[2.25rem]"
    aria-hidden>
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="#FF6464"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-[2.25rem] w-[2.25rem]"
    aria-hidden>
    <path
      d="M5 12.5l4 4 10-10"
      stroke="#BFE7FF"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function ComparisonSection({
  chip,
  title,
  othersLabel,
  wceLabel,
  rows,
  othersFooter,
  cta,
}: ComparisonSectionProps) {
  return (
    <section className="flex flex-col items-center w-full lg:px-0 px-[2rem] pt-[9rem] pb-[9rem] lg:pb-[20rem] lg:pt-[15rem]">
      <div className="flex flex-col items-center max-w-full w-[min(135.25rem,100%)] gap-[4rem]">
        <div className="flex flex-col items-center text-center">
          {chip && (
            <FadeIn delay={0}>
              <PipeChip color="#b8d2e2">{chip}</PipeChip>
            </FadeIn>
          )}
          {title && (
            <FadeIn
              delay={0.1}
              className="richtext richtext-center bg prose-p:font-news prose-p:text-[6rem] prose-p:leading-[1.1] prose-p:tracking-[-0.25rem] prose-p:text-white/90 prose-em:font-news prose-em:italic prose-em:underline prose-headings:font-news prose-headings:leading-[1.1] prose-headings:tracking-[-0.25rem] prose-headings:text-white/90 prose-headings:text-[4.5rem] lg:prose-headings:text-[6rem]">
              <PortableText
                value={title}
                components={richTextComponent}
              />
            </FadeIn>
          )}
        </div>

        <FadeIn
          delay={0.2}
          className="flex flex-col items-stretch w-full lg:flex-row gap-[2.5rem] lg:h-[100rem]">
          {/* Others */}
          <div className="flex flex-col flex-1 border gap-[4rem] rounded-[2rem] border-white/[4%] bg-[#141414] p-[4rem] w-full h-full">
            <div className="flex flex-col gap-[1.5rem]">
              <div className="flex px-[2rem] py-[2.25rem]">
                <p className="m-0 font-news text-[4rem] leading-[1.1] tracking-[-0.125rem] text-white/90">
                  {othersLabel}
                </p>
              </div>
              <div className="flex flex-col">
                {rows?.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center border-b last:border-b-0 gap-[1.25rem] border-white/10 py-[2rem]">
                    <span className="flex items-center rounded-full bg-white/[0.06] p-[0.875rem] w-[4rem] h-[4rem] shrink-0">
                      <CrossIcon />
                    </span>
                    <span className="font-geist text-[2.25rem] tracking-[0.0225rem] text-white/[0.86]">{r.others}</span>
                  </div>
                ))}
              </div>
            </div>
            {othersFooter && (
              <div className="flex justify-center items-center h-[7rem] rounded-[1.25rem] bg-white/[4%]">
                <p className="m-0 font-medium text-white opacity-50 font-geist text-[2rem]">{othersFooter}</p>
              </div>
            )}
          </div>

          {/* WCE */}
          <div className="flex flex-col flex-1 bg-white gap-[4rem] rounded-[2rem] p-[4rem]">
            <div className="flex flex-col gap-[1.5rem]">
              <div className="flex px-[2rem] py-[2.25rem]">
                <p className="m-0 font-news text-[4rem] leading-[1.1] tracking-[-0.125rem] text-black/90">{wceLabel}</p>
              </div>
              <div className="flex flex-col">
                {rows?.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center border-b last:border-b-0 gap-[1.25rem] border-black/10 py-[2rem]">
                    <span className="flex items-center rounded-full bg-black/[0.88] p-[1rem]">
                      <CheckIcon />
                    </span>
                    <span className="font-geist text-[2.25rem] tracking-[0.0225rem] text-black/[0.72]">{r.wce}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full">
              <CTAButton
                data={cta ?? {}}
                fullWidth
                className=""
              />
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
